import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemSettingControllerService } from '../../api/services/SystemSettingControllerService';
import { useErrorHandler } from '../../utils/errorHandler';
import { useToast } from '../../hooks/useToast';
import { api } from '../../api/api';
import { useHasPermission } from '../../utils/permissions';
import { isModuleLicensed } from '../../utils/license';
import { useStore } from '../../../store';

// Define the PlanningHorizonSettings type
interface PlanningHorizonSettings {
  defaultDays: number;
  minDays: number;
  maxDays: number;
  allowOverride: boolean;
  /** Max. days into the future for the planning start (default 7). */
  startMaxDaysAhead?: number;
  /** Maximum offset (days) for long-running products. The product offset must lie between 1 and this value. */
  longTermOffsetMaxDays?: number;
  availabilityExtensionDays?: number;
  productionDayEndHour?: number;
  /** Number of weeks that intermediate product forecasts are carried forward to the same weekday. 0 = off, default 4. */
  intermediateForecastWeeksAhead?: number;
}

/** Rough planning settings (planning.*, docs/CONCEPT_Grobplanung.md chapter 5a). */
interface RoughPlanningSettings {
  horizonDefaultDays: number;
  horizonMinDays: number;
  horizonMaxDays: number;
  horizonAllowOverride: boolean;
  thresholdWarnPercent: number;
  thresholdCriticalPercent: number;
  dayStartTime: string;
  runTimeLimitSeconds: number;
  profileDeviationWarnPercent: number;
}

interface GeneralSettings {
  country: string;
  region: string;
  global: boolean;
}

// German federal states - the value is the code used by the API
const GERMAN_STATES = [
  'BW', 'BY', 'BE', 'BB', 'HB', 'HH', 'HE', 'MV', 'NI', 'NW', 'RP', 'SL', 'SN', 'ST', 'SH', 'TH'
] as const;

import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { NumericTextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
const SystemSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const hasSettingsEditPermission = useHasPermission('system:settings:edit');
  const userProfile = useStore((state) => state.userProfile);
  // The rough planning section only shows when the planning area has the PLANNING module
  const planningLicensed = isModuleLicensed(userProfile, 'PLANNING');
  const [roughPlanningSettings, setRoughPlanningSettings] = useState<RoughPlanningSettings | null>(null);
  
  const [settings, setSettings] = useState<PlanningHorizonSettings>({
    defaultDays: 1,
    allowOverride: true,
    minDays: 1,
    maxDays: 1,
    startMaxDaysAhead: 7,
    longTermOffsetMaxDays: 5,
    availabilityExtensionDays: 2,
    productionDayEndHour: 6,
    intermediateForecastWeeksAhead: 4
  });

  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>({
    country: 'DE',
    region: '',
    global: false
  });

  const [maxProductionLeadTimeDays, setMaxProductionLeadTimeDays] = useState<number>(0);

  const [campaignSplittingEnabled, setCampaignSplittingEnabled] = useState<boolean>(true);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [tenantLogoPreviewUrl, setTenantLogoPreviewUrl] = useState<string | null>(null);
  const [tenantLogoLoading, setTenantLogoLoading] = useState(false);
  const [tenantLogoUploading, setTenantLogoUploading] = useState(false);
  const tenantLogoInputRef = useRef<HTMLInputElement>(null);
  const tenantLogoObjectUrlRef = useRef<string | null>(null);

  const MAX_LOGO_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
  const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSettings is a local function; runs only on mount
  }, []);

  useEffect(() => {
    loadTenantLogo();
    return () => {
      if (tenantLogoObjectUrlRef.current) {
        URL.revokeObjectURL(tenantLogoObjectUrlRef.current);
        tenantLogoObjectUrlRef.current = null;
      }
    };
  }, []);

  const loadTenantLogo = async () => {
    setTenantLogoLoading(true);
    if (tenantLogoObjectUrlRef.current) {
      URL.revokeObjectURL(tenantLogoObjectUrlRef.current);
      tenantLogoObjectUrlRef.current = null;
    }
    setTenantLogoPreviewUrl(null);
    try {
      const res = await api.get('/api/tenants/current/logo', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      tenantLogoObjectUrlRef.current = url;
      setTenantLogoPreviewUrl(url);
    } catch {
      setTenantLogoPreviewUrl(null);
    } finally {
      setTenantLogoLoading(false);
    }
  };

  const handleUploadTenantLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      showToast('error', t('systemSettings.saveFailed'), t('systemSettings.tenantLogo.fileTooBig'));
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      showToast('error', t('systemSettings.saveFailed'), t('systemSettings.tenantLogo.invalidType'));
      return;
    }
    setTenantLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/api/tenants/current/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await loadTenantLogo();
      showToast('success', t('systemSettings.saved'), t('systemSettings.tenantLogo.uploadSuccess'));
    } catch (err) {
      handleError(err, 'uploading tenant logo');
      showToast('error', t('systemSettings.saveFailed'), t('systemSettings.tenantLogo.uploadError'));
    } finally {
      setTenantLogoUploading(false);
    }
  };

  const handleRemoveTenantLogo = async () => {
    setTenantLogoUploading(true);
    try {
      await api.delete('/api/tenants/current/logo');
      await loadTenantLogo();
      showToast('success', t('systemSettings.saved'), t('systemSettings.tenantLogo.removeSuccess'));
    } catch (err) {
      handleError(err, 'removing tenant logo');
      showToast('error', t('systemSettings.saveFailed'), t('systemSettings.tenantLogo.removeError'));
    } finally {
      setTenantLogoUploading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const [horizonData, generalData, maxLeadTimeData, schedulingData] = await Promise.all([
        SystemSettingControllerService.getPlanningHorizonSettings(),
        SystemSettingControllerService.getGeneralSettings(),
        SystemSettingControllerService.getMaxProductionLeadTimeDefault(),
        api.get('/api/system-settings/scheduling').then(r => r.data).catch(() => ({ campaignSplittingEnabled: true }))
      ]);
      setSettings({
        ...horizonData,
        defaultDays: 1,
        minDays: 1,
        maxDays: 1,
        allowOverride: horizonData.allowOverride || false,
        startMaxDaysAhead: horizonData.startMaxDaysAhead != null ? Math.round(horizonData.startMaxDaysAhead) : 7,
        longTermOffsetMaxDays: horizonData.longTermOffsetMaxDays != null ? Math.round(horizonData.longTermOffsetMaxDays) : 5,
        availabilityExtensionDays: horizonData.availabilityExtensionDays ?? 2,
        productionDayEndHour: horizonData.productionDayEndHour ?? 6,
        intermediateForecastWeeksAhead: horizonData.intermediateForecastWeeksAhead != null ? Math.round(horizonData.intermediateForecastWeeksAhead) : 4
      } as PlanningHorizonSettings);
      setGeneralSettings({
        country: generalData.country ?? 'DE',
        region: generalData.region ?? '',
        global: generalData.global === true
      });
      setMaxProductionLeadTimeDays((maxLeadTimeData?.maxProductionLeadTimeHours ?? 0) / 24);
      setCampaignSplittingEnabled(schedulingData?.campaignSplittingEnabled !== false);
      if (planningLicensed) {
        const roughPlanningData = await api.get('/api/system-settings/rough-planning').then(r => r.data);
        setRoughPlanningSettings(roughPlanningData as RoughPlanningSettings);
      } else {
        setRoughPlanningSettings(null);
      }
    } catch (error) {
      handleError(error, 'loading system settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const settingsToSave = { ...settings, defaultDays: 1, minDays: 1, maxDays: 1 };
      await Promise.all([
        SystemSettingControllerService.updatePlanningHorizonSettings(settingsToSave),
        SystemSettingControllerService.updateGeneralSettings(generalSettings),
        SystemSettingControllerService.updateMaxProductionLeadTimeDefault({
          maxProductionLeadTimeHours: Math.round(maxProductionLeadTimeDays * 24)
        }),
        api.put('/api/system-settings/scheduling', { campaignSplittingEnabled }),
        ...(roughPlanningSettings ? [api.put('/api/system-settings/rough-planning', roughPlanningSettings)] : [])
      ]);
      showToast('success', t('systemSettings.saved'), '');
    } catch (error) {
      handleError(error, 'saving system settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
  };

  const countryDataSource = [
    { value: 'DE', text: t('systemSettings.general.countryValue') }
  ];

  const regionDataSource = [
    { value: '', text: t('systemSettings.general.regionPlaceholder') },
    ...GERMAN_STATES.map(code => ({ value: code, text: t(`systemSettings.general.region.${code}`) }))
  ];

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          {t('routes.systemSettings')}
        </h2>
      </div>

      {/* General Settings Section – Standort Backstube */}
      <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('systemSettings.general.title')}
            </h2>
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
              {t('systemSettings.general.badge')}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {t('systemSettings.general.description')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.general.country')} *
            </label>
            <DropDownListComponent
              value={generalSettings.country}
              dataSource={countryDataSource}
              fields={{ text: 'text', value: 'value' }}
              cssClass="w-full"
              readOnly={true}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.general.region')}
            </label>
            <DropDownListComponent
              value={generalSettings.region}
              dataSource={regionDataSource}
              fields={{ text: 'text', value: 'value' }}
              change={(e: { value: string }) => setGeneralSettings((prev) => ({ ...prev, region: e.value ?? '' }))}
              placeholder={t('systemSettings.general.regionPlaceholder')}
              cssClass="w-full"
              allowFiltering={true}
            />
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center space-x-3">
              <CheckBoxComponent
                checked={generalSettings.global}
                change={(e: { checked: boolean }) => setGeneralSettings((prev) => ({ ...prev, global: e.checked }))}
              />
              <span className="text-sm font-medium text-gray-700">
                {t('systemSettings.general.global')}
              </span>
              <span
                className="ml-1 text-blue-500 cursor-help"
                title={t('systemSettings.general.globalHelp')}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </span>
            </label>
            <p className="mt-1 text-xs text-gray-500">
              {t('systemSettings.general.globalHelp')}
            </p>
          </div>
        </div>

      </div>

      {/* Kundenlogo */}
      <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('systemSettings.tenantLogo.title')}
            </h2>
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
              {t('systemSettings.tenantLogo.badge')}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {t('systemSettings.tenantLogo.description')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {tenantLogoLoading ? (
            <div className="h-20 w-32 rounded border border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
              …
            </div>
          ) : tenantLogoPreviewUrl ? (
            <img
              src={tenantLogoPreviewUrl}
              alt="Kundenlogo"
              className="h-20 w-auto max-w-[200px] object-contain rounded border border-gray-200"
            />
          ) : (
            <div className="h-20 w-32 rounded border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-sm text-gray-500">
              {t('systemSettings.tenantLogo.noLogo')}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={tenantLogoInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif"
              className="hidden"
              onChange={handleUploadTenantLogo}
            />
            <ButtonComponent
              onClick={() => tenantLogoInputRef.current?.click()}
              disabled={tenantLogoUploading || !hasSettingsEditPermission}
              cssClass="e-outline"
            >
              {tenantLogoUploading ? t('actions.saving') : t('systemSettings.tenantLogo.upload')}
            </ButtonComponent>
            <ButtonComponent
              onClick={handleRemoveTenantLogo}
              disabled={tenantLogoUploading || !tenantLogoPreviewUrl || !hasSettingsEditPermission}
              cssClass="e-outline e-small"
            >
              {t('systemSettings.tenantLogo.remove')}
            </ButtonComponent>
          </div>
        </div>
      </div>

      {/* Max. production lead time (global default) - system settings only */}
      <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('systemSettings.maxProductionLeadTime.title')}
            </h2>
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
              {t('systemSettings.maxProductionLeadTime.badge')}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            {t('systemSettings.maxProductionLeadTime.description')}
          </p>
        </div>
        <div className="max-w-xs">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('systemSettings.maxProductionLeadTime.label')}
          </label>
          <NumericTextBoxComponent
            value={maxProductionLeadTimeDays}
            change={(e: { value: number | null }) => setMaxProductionLeadTimeDays(e.value ?? 0)}
            format="n0"
            min={0}
            placeholder={t('systemSettings.maxProductionLeadTime.placeholder')}
            cssClass="w-full"
          />
        </div>
      </div>

      {/* Scheduling Settings Section */}
      <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('systemSettings.planningHorizon.title')}
            </h2>
            <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
              Scheduling
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Default planning horizon - fixed at 1 day */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.defaultDays')} *
            </label>
            <NumericTextBoxComponent
              value={1}
              enabled={false}
              cssClass="w-full"
              placeholder={t('systemSettings.planningHorizon.defaultDaysPlaceholder')}
              decimals={0}
              showSpinButton={false}
              format="N0"
              step={1}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('systemSettings.planningHorizon.singleDayFixedHelp')}
            </p>
          </div>

          {/* Allow Override */}
          <div>
            <label className="flex items-center space-x-3">
              <CheckBoxComponent
                checked={settings.allowOverride}
                change={(e: { checked: boolean }) => setSettings((prev: PlanningHorizonSettings) => ({ ...prev, allowOverride: e.checked }))}
              />
              <span className="text-sm font-medium text-gray-700">
                {t('systemSettings.planningHorizon.allowOverride')}
              </span>
              <span 
                className="ml-1 text-blue-500 cursor-help"
                title={t('systemSettings.planningHorizon.allowOverrideHelp')}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </span>
            </label>
          </div>

          {/* Min planning horizon - fixed at 1 day */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.minDays')} *
            </label>
            <NumericTextBoxComponent
              value={1}
              enabled={false}
              cssClass="w-full"
              placeholder={t('systemSettings.planningHorizon.minDaysPlaceholder')}
              decimals={0}
              showSpinButton={false}
              format="N0"
              step={1}
            />
          </div>

          {/* Max planning horizon - fixed at 1 day */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.maxDays')} *
            </label>
            <NumericTextBoxComponent
              value={1}
              enabled={false}
              cssClass="w-full"
              placeholder={t('systemSettings.planningHorizon.maxDaysPlaceholder')}
              decimals={0}
              showSpinButton={false}
              format="N0"
              step={1}
            />
          </div>

          {/* Max. days into the future for the planning start */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.startMaxDaysAhead')} *
            </label>
            <NumericTextBoxComponent
              value={Math.round(settings.startMaxDaysAhead ?? 7)}
              onChange={(e: { value: number }) => setSettings((prev: PlanningHorizonSettings) => ({ ...prev, startMaxDaysAhead: Math.round(e.value ?? 7) }))}
              min={1}
              max={365}
              cssClass="w-full"
              placeholder={t('systemSettings.planningHorizon.startMaxDaysAheadPlaceholder')}
              decimals={0}
              showSpinButton={true}
              format="N0"
              step={1}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('systemSettings.planningHorizon.startMaxDaysAheadHelp')}
            </p>
          </div>

          {/* Maximum offset for long-running products (days) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.longTermOffsetMaxDays')} *
            </label>
            <NumericTextBoxComponent
              value={Math.round(settings.longTermOffsetMaxDays ?? 5)}
              onChange={(e: { value: number }) => setSettings((prev: PlanningHorizonSettings) => ({ ...prev, longTermOffsetMaxDays: Math.round(e.value ?? 5) }))}
              min={1}
              max={31}
              cssClass="w-full"
              placeholder={t('systemSettings.planningHorizon.longTermOffsetMaxDaysPlaceholder')}
              decimals={0}
              showSpinButton={true}
              format="N0"
              step={1}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('systemSettings.planningHorizon.longTermOffsetMaxDaysHelp')}
            </p>
          </div>

          {/* Additional days for resource availability extension */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.availabilityExtensionDays')}
            </label>
            <NumericTextBoxComponent
              value={Math.round(settings.availabilityExtensionDays ?? 2)}
              onChange={(e: { value: number }) => setSettings((prev: PlanningHorizonSettings) => ({ ...prev, availabilityExtensionDays: Math.round(e.value ?? 2) }))}
              min={0}
              max={30}
              cssClass="w-full"
              placeholder={t('systemSettings.planningHorizon.availabilityExtensionDaysPlaceholder')}
              decimals={0}
              showSpinButton={true}
              format="N0"
              step={1}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('systemSettings.planningHorizon.availabilityExtensionDaysHelp')}
            </p>
          </div>

          {/* Production day end hour: production day = [previous evening at X o'clock, planning day at X o'clock]; effective start = max(start, NOW) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.productionDayEndHour')}
            </label>
            <NumericTextBoxComponent
              value={Math.round(settings.productionDayEndHour ?? 6)}
              onChange={(e: { value: number }) => setSettings((prev: PlanningHorizonSettings) => ({ ...prev, productionDayEndHour: Math.round(e.value ?? 6) }))}
              min={0}
              max={23}
              cssClass="w-full"
              placeholder="6"
              decimals={0}
              showSpinButton={true}
              format="N0"
              step={1}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('systemSettings.planningHorizon.productionDayEndHourHelp')}
            </p>
          </div>

          {/* Intermediate product forecast: weekly carry-forward */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('systemSettings.planningHorizon.intermediateForecastWeeksAhead', 'Vorprodukt-Prognosen (Wochen)')}
            </label>
            <NumericTextBoxComponent
              value={Math.round(settings.intermediateForecastWeeksAhead ?? 4)}
              onChange={(e: { value: number }) => setSettings((prev: PlanningHorizonSettings) => ({ ...prev, intermediateForecastWeeksAhead: Math.round(e.value ?? 4) }))}
              min={0}
              max={52}
              cssClass="w-full"
              placeholder="4"
              decimals={0}
              showSpinButton={true}
              format="N0"
              step={1}
            />
            <p className="mt-1 text-xs text-gray-500">
              {t('systemSettings.planningHorizon.intermediateForecastWeeksAheadHelp', 'Beim Speichern eines Plans werden manuell eingegebene Vorprodukt-Forecasts auf den gleichen Wochentag fuer N Wochen in die Zukunft fortgeschrieben (analog Endprodukt-CSV-Import). 0 = aus.')}
            </p>
          </div>
        </div>

      </div>

      {/* Scheduling section */}
      <div className="border-b border-gray-200 pb-6 mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('systemSettings.scheduling.title', 'Planung')}
        </h3>
        <div className="flex items-center">
          <CheckBoxComponent
            checked={campaignSplittingEnabled}
            change={(e: any) => setCampaignSplittingEnabled(e.checked === true)}
            disabled={!hasSettingsEditPermission}
            label={t('systemSettings.scheduling.campaignSplittingEnabled', 'Kampagnen automatisch splitten')}
          />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {t('systemSettings.scheduling.campaignSplittingEnabledHelp',
             'Wenn aktiv, werden Kampagnen deren Ofen-Chargen die verfügbaren Öfen übersteigen automatisch auf mehrere Kampagnen aufgeteilt. Wenn inaktiv, bleibt die Kampagne zusammen und der Scheduler arbeitet die Ofen-Chargen sequentiell ab.')}
        </p>
      </div>

      {/* Rough planning section (PLANNING module only) */}
      {roughPlanningSettings && (
        <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200" data-testid="rough-planning-settings">
          <div className="mb-6">
            <div className="flex items-center mb-2">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('systemSettings.roughPlanning.title', 'Grobplanung')}
              </h2>
              <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                Planning
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {t('systemSettings.roughPlanning.description', 'Horizont, Ampelschwellen, Tageswechsel und Laufzeit der Grobplanung für diesen Planungsbereich.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {([
              ['horizonDefaultDays', 'Standardhorizont (Tage)', 1, 28],
              ['horizonMinDays', 'Minimaler Horizont (Tage)', 1, 28],
              ['horizonMaxDays', 'Maximaler Horizont (Tage)', 1, 28],
              ['thresholdWarnPercent', 'Ampel gelb ab (%)', 1, 200],
              ['thresholdCriticalPercent', 'Ampel rot ab (%)', 1, 200],
              ['runTimeLimitSeconds', 'Laufzeitbudget (Sekunden)', 10, 3600],
              ['profileDeviationWarnPercent', 'Warnschwelle Belastungsprofil (%)', 1, 100],
            ] as const).map(([key, fallback, min, max]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t(`systemSettings.roughPlanning.${key}`, fallback)}
                </label>
                <NumericTextBoxComponent
                  value={roughPlanningSettings[key]}
                  change={(e: { value: number | null }) => setRoughPlanningSettings((prev) =>
                    prev ? { ...prev, [key]: e.value != null ? Math.round(e.value) : prev[key] } : prev)}
                  min={min}
                  max={max}
                  decimals={0}
                  format="N0"
                  step={1}
                  showSpinButton={true}
                  enabled={hasSettingsEditPermission}
                  cssClass="w-full"
                />
              </div>
            ))}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('systemSettings.roughPlanning.dayStartTime', 'Tageswechsel (HH:mm)')}
              </label>
              <input
                type="time"
                className="e-input w-full border border-gray-300 rounded px-2 py-1"
                value={roughPlanningSettings.dayStartTime}
                disabled={!hasSettingsEditPermission}
                onChange={(e) => {
                  const next = e.target.value;
                  setRoughPlanningSettings((prev) => (prev && next ? { ...prev, dayStartTime: next } : prev));
                }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('systemSettings.roughPlanning.dayStartTimeHelp', 'Ab dieser Uhrzeit gehört die Schicht zum folgenden Produktionstag.')}
              </p>
            </div>

            <div>
              <label className="flex items-center space-x-3">
                <CheckBoxComponent
                  checked={roughPlanningSettings.horizonAllowOverride}
                  disabled={!hasSettingsEditPermission}
                  change={(e: { checked: boolean }) => setRoughPlanningSettings((prev) =>
                    prev ? { ...prev, horizonAllowOverride: e.checked === true } : prev)}
                />
                <span className="text-sm font-medium text-gray-700">
                  {t('systemSettings.roughPlanning.horizonAllowOverride', 'Planer darf den Horizont im Wizard ändern')}
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Single Save/Reset for all settings */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <ButtonComponent
          onClick={handleReset}
          cssClass="e-outline"
          disabled={isSaving}
        >
          {t('actions.reset')}
        </ButtonComponent>
        <ButtonComponent
          onClick={handleSave}
          cssClass="e-primary"
          disabled={isSaving}
        >
          {isSaving ? t('actions.saving') : t('actions.save')}
        </ButtonComponent>
      </div>
    </div>
  );
};

export default SystemSettingsPage;
